import { FC, useCallback, useContext, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Dropdown, useSimpleInputState } from "capitalrx-components-2.0/lib/forms";
import { Buttons, HorizontalLine, Message, Messages, useAlert } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Subheading } from "capitalrx-components-2.0/lib/typography";
import { formatDollars } from "capitalrx-utilities";
import Decimal from "decimal.js";

import { useCancelMedicarePrescriptionPaymentPlanMutation } from "@/App/api/medicarePrescriptionPaymentPlan";
import { ThemedButton } from "@/App/components";
import { MemberEligibilityContext } from "@/App/context/MemberEligibilityContext";
import { useIsLoggedInUser } from "@/App/hooks";
import { usePlanYear } from "@/App/pages";
import { useBalance } from "@/App/pages/MedicarePrescriptionPaymentPlan/Participation/useBalance";
import { PaymentMethod } from "@/App/types/medicarePrescriptionPaymentPlan";
import { getUsersTimezone } from "@/App/utils/dates";
import { showMedicarePrescriptionPaymentPlanPaymentInfo } from "@/App/utils/featureFlags";
import { getAllowedPaymentMethods, getCustomerSupportNumber } from "@/App/utils/utils";

import { StripePaymentElement, StripePaymentForm } from "../../Stripe/StripePaymentForm";

interface OptOutFormData {
  readonly outstandingBalanceStrategy: PaymentMethod | "continue_monthly_payments" | undefined;
}

const TRANSLATION_PATH = "MEDICARE_PRESCRIPTION_PAYMENT_PLAN.participationDetailsOptOutForm";

export const OptOutForm: FC = () => {
  const [isTryingToOptOut, setIsTryingToOptOut] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadingError, setHasLoadingError] = useState(false);

  const { t: translate } = useTranslation();
  const planYear = usePlanYear();

  const { memberEligibility } = useContext(MemberEligibilityContext);
  const stripeFormRef = useRef<HTMLButtonElement>();

  const { mutateAsync: cancelParticipation } = useCancelMedicarePrescriptionPaymentPlanMutation();
  const [outstandingBalanceStrategy, setOutstandingBalanceStrategy] = useSimpleInputState<OptOutFormData["outstandingBalanceStrategy"]>();
  const { isBalanceLoading, isBalanceError, hasBalance, hasProcessingPayments, invoiceBalance, accumulationBalance } = useBalance(planYear);

  const { isCompressed } = useResponsive({
    isCompressed: {
      mobile: true,
      desktop: false,
    },
  });
  const showAlert = useAlert();
  const { isLoggedInUser } = useIsLoggedInUser();

  const customerSupportNumber = getCustomerSupportNumber(memberEligibility);
  const allowedPaymentMethods = getAllowedPaymentMethods(memberEligibility);

  const maxOfInvoiceAndAccumBalances = Decimal.max(accumulationBalance, invoiceBalance);

  const onStripeError = useCallback(() => {
    showAlert({
      message: translate(`${TRANSLATION_PATH}.unableToContactPaymentVendor`),
      type: "error",
      style: "banner",
    });
    setOutstandingBalanceStrategy(undefined, outstandingBalanceStrategy);
  }, [outstandingBalanceStrategy, setOutstandingBalanceStrategy]);

  const handleOptOut = async (customerId?: string) => {
    try {
      await cancelParticipation({
        payment_method: ["card", "check", "ach"].includes(outstandingBalanceStrategy)
          ? (outstandingBalanceStrategy as PaymentMethod)
          : undefined,
        vendor_customer_id: customerId,
        tz: getUsersTimezone(),
        plan_year: planYear,
      });

      showAlert({
        message: translate(`${TRANSLATION_PATH}.optedOutSuccessfully`),
        type: "success",
        style: "banner",
      });
    } catch (err) {
      showAlert({
        message:
          typeof err === "string"
            ? err
            : err.response?.data?.message || err.data?.message || translate(`${TRANSLATION_PATH}.unableToCancelParticipation`),
        type: "error",
        style: "banner",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitOptOutForm = async () => {
    setIsLoading(true);
    if (outstandingBalanceStrategy === "card" || outstandingBalanceStrategy === "ach") {
      stripeFormRef?.current?.click();
    } else {
      await handleOptOut();
    }
  };

  const onCancelOptOut = () => {
    setOutstandingBalanceStrategy(undefined, outstandingBalanceStrategy);
    setIsTryingToOptOut(false);
  };

  const handleLoadingError = () => {
    setHasLoadingError(true);
  };

  const StackOrShelf = isCompressed ? Stack : Shelf;

  return (
    <>
      {!isTryingToOptOut && (
        <StackOrShelf>
          <ThemedButton testId="opt-out" disabled={!isLoggedInUser} appearance="secondary" onClick={() => setIsTryingToOptOut(true)}>
            {translate(`${TRANSLATION_PATH}.optOut`)}
          </ThemedButton>
          <Shelf.Spacer />
        </StackOrShelf>
      )}
      {isTryingToOptOut && (
        <>
          <HorizontalLine />
          <Stack spacing="medium">
            <Subheading>{translate(`${TRANSLATION_PATH}.optOut`)}</Subheading>
            <Body>
              {translate(`${TRANSLATION_PATH}.optOutFormDescription`)}
              {!!customerSupportNumber && ` ${translate(`${TRANSLATION_PATH}.orCallingCustomerCare`)} ${customerSupportNumber}`}.
            </Body>
            {showMedicarePrescriptionPaymentPlanPaymentInfo() && !isBalanceLoading && !isBalanceError && hasBalance && (
              <Body>
                <Trans
                  i18nKey={`${TRANSLATION_PATH}.outstandingBalance`}
                  values={{ maxOfInvoiceAndAccumBalances: formatDollars(maxOfInvoiceAndAccumBalances.toNumber()) }}
                />
              </Body>
            )}
            <Body>{translate(`${TRANSLATION_PATH}.optOutNewOutOfPocket`)}</Body>
          </Stack>
          {hasBalance && (
            <Dropdown
              testId="payment-method"
              label={translate(`${TRANSLATION_PATH}.payOutstandingBalance`)}
              value={outstandingBalanceStrategy}
              onChange={setOutstandingBalanceStrategy}
              placeholder={translate(`${TRANSLATION_PATH}.select`)}
            >
              {showMedicarePrescriptionPaymentPlanPaymentInfo() && allowedPaymentMethods.includes("card") && !hasProcessingPayments && (
                <Dropdown.Option value="card">{translate(`${TRANSLATION_PATH}.payNowViaCard`)}</Dropdown.Option>
              )}
              {showMedicarePrescriptionPaymentPlanPaymentInfo() && allowedPaymentMethods.includes("ach") && !hasProcessingPayments && (
                <Dropdown.Option value="ach">{translate(`${TRANSLATION_PATH}.payNowViaBankAccount`)}</Dropdown.Option>
              )}
              {showMedicarePrescriptionPaymentPlanPaymentInfo() && allowedPaymentMethods.includes("check") && (
                <Dropdown.Option value="check">{translate(`${TRANSLATION_PATH}.payViaCheck`)}</Dropdown.Option>
              )}
              <Dropdown.Option value="continue_monthly_payments">{translate(`${TRANSLATION_PATH}.continueMonthlyPayment`)}</Dropdown.Option>
            </Dropdown>
          )}

          {(outstandingBalanceStrategy === "card" || outstandingBalanceStrategy === "ach") && hasBalance && (
            <StripePaymentElement onError={onStripeError} amount={maxOfInvoiceAndAccumBalances} paymentMethod={outstandingBalanceStrategy}>
              <StripePaymentForm
                amount={maxOfInvoiceAndAccumBalances}
                paymentMethod={outstandingBalanceStrategy}
                submitRef={stripeFormRef}
                handlePaymentSuccess={handleOptOut}
                setLoading={setIsLoading}
                handleLoadingError={handleLoadingError}
                showPaymentMethodForm={true}
              />
            </StripePaymentElement>
          )}
          {(outstandingBalanceStrategy === "card" || outstandingBalanceStrategy === "ach") && hasLoadingError && (
            <Message appearance={Messages.Error} visible={true}>
              {translate(`${TRANSLATION_PATH}.unableToLoadStripe`)} <a href={`tel:${customerSupportNumber}`}>{customerSupportNumber}</a>.
            </Message>
          )}

          <StackOrShelf spacing="small">
            <ThemedButton
              testId="opt-out"
              disabled={!isLoggedInUser || isLoading || (!outstandingBalanceStrategy && hasBalance)}
              appearance={Buttons.Destructive.Primary.Large}
              onClick={onSubmitOptOutForm}
            >
              {translate(`${TRANSLATION_PATH}.optOutCallToAction`)}
            </ThemedButton>
            <ThemedButton testId="cancel-opting-out" appearance="secondary" onClick={onCancelOptOut}>
              {translate(`${TRANSLATION_PATH}.cancel`)}
            </ThemedButton>
            <Shelf.Spacer />
          </StackOrShelf>
        </>
      )}
    </>
  );
};
